import { phoneHoursProblem, phoneWordsProblem, ringSecondsProblem } from '../calls/phoneSystem';

/**
 * Settings › Integrations › Twilio (piece 15; decision 5820; T2801): the card
 * that holds the communications vendor's account, sealed token and company
 * number, which the text and call seam reads before the environment's three
 * keys (decision 5120, amended by 5820).
 *
 * 🔨 The auth token is a sealed row: the screen learns that one is held and its
 * last four characters, never the token. The account SID and the company
 * number are not secrets and are held in the clear.
 */

/** 🔒 The three rows, each read before its environment key; an absent row leaves the environment's value in force. */
export const TWILIO_ACCOUNT_SID_KEY = 'communications.twilioAccountSid';
export const TWILIO_AUTH_TOKEN_KEY = 'communications.twilioAuthToken';
export const TWILIO_FROM_NUMBER_KEY = 'communications.twilioFromNumber';
/** 🔒 Decisions 11781 and 11783: the phone settings Step 1 holds beside the account, each shipped unset. */
export const VOICEMAIL_GREETING_KEY = 'communications.voicemailGreeting';
export const MENU_WORDS_KEY = 'communications.menuWords';
export const PHONE_BUSINESS_HOURS_KEY = 'communications.businessHours';
export const RING_SECONDS_KEY = 'communications.ringSeconds';
export const REP_FIRST_SECONDS_KEY = 'communications.repFirstSeconds';
/** 🔨 Named one by one, because the guard in `apps/web/src/settingsKeys.test.ts` resolves a named constant and not a spread. */
export const TWILIO_PHONE_SETTING_KEYS: readonly string[] = [VOICEMAIL_GREETING_KEY, MENU_WORDS_KEY, PHONE_BUSINESS_HOURS_KEY, RING_SECONDS_KEY, REP_FIRST_SECONDS_KEY];
/** 🔒 Decision 12000: the five App Calling rows the phone app's access token reads, each from its row alone. */
export const TWILIO_API_KEY_SID_KEY = 'communications.twilioApiKeySid';
export const TWILIO_API_KEY_SECRET_KEY = 'communications.twilioApiKeySecret';
export const TWILIO_TWIML_APP_SID_KEY = 'communications.twilioTwimlAppSid';
export const TWILIO_PUSH_CREDENTIAL_IOS_KEY = 'communications.twilioPushCredentialIos';
export const TWILIO_PUSH_CREDENTIAL_ANDROID_KEY = 'communications.twilioPushCredentialAndroid';
/** 🔨 Named one by one, because the guard in `apps/web/src/settingsKeys.test.ts` resolves a named constant and not a spread. */
export const TWILIO_APP_CALLING_KEYS: readonly string[] = [TWILIO_API_KEY_SID_KEY, TWILIO_API_KEY_SECRET_KEY, TWILIO_TWIML_APP_SID_KEY, TWILIO_PUSH_CREDENTIAL_IOS_KEY, TWILIO_PUSH_CREDENTIAL_ANDROID_KEY];
/** 🔨 Named one by one, because the guard in `apps/web/src/settingsKeys.test.ts` resolves a named constant and not a spread. A save of any of them rebinds the seam. */
export const TWILIO_SETTING_KEYS: readonly string[] = [TWILIO_ACCOUNT_SID_KEY, TWILIO_AUTH_TOKEN_KEY, TWILIO_FROM_NUMBER_KEY, TWILIO_API_KEY_SID_KEY, TWILIO_API_KEY_SECRET_KEY, TWILIO_TWIML_APP_SID_KEY, TWILIO_PUSH_CREDENTIAL_IOS_KEY, TWILIO_PUSH_CREDENTIAL_ANDROID_KEY];

/** The environment key each row stands in front of (decision 5120). */
export const TWILIO_ENVIRONMENT_KEY_OF: Readonly<Record<TwilioField, string>> = {
  accountSid: 'TWILIO_ACCOUNT_SID',
  authToken: 'TWILIO_AUTH_TOKEN',
  fromNumber: 'TWILIO_FROM_NUMBER',
};

export const TWILIO_FIELDS = ['accountSid', 'authToken', 'fromNumber'] as const;
export type TwilioField = (typeof TWILIO_FIELDS)[number];

/** 🔒 Decision 12000: the App Calling rows' fields on the binding; none has an environment key. */
export const TWILIO_APP_FIELDS = ['apiKeySid', 'apiKeySecret', 'twimlAppSid', 'pushCredentialIos', 'pushCredentialAndroid'] as const;
export type TwilioAppField = (typeof TWILIO_APP_FIELDS)[number];

/** The card's own page, and the API's reading and Test Connection. 🔨 The paths are relative to the API root (piece 1). */
export const TWILIO_SETUP_ROUTE = '/settings/integrations/twilio';
export const TWILIO_SETUP_PATH = '/settings/integrations/twilio';
export const TWILIO_TEST_PATH = '/settings/integrations/twilio/test';

/** Where the account SID and the auth token are read in Twilio's console. */
export const TWILIO_CONSOLE_URL = 'https://console.twilio.com/';

/** 🔒 The public route an inbound text is posted to (decision 5120), under the API's own root. */
export const TWILIO_INBOUND_TEXT_ROUTE = '/texts/inbound';

/** The address to paste into the number's messaging webhook in Twilio's console, built as Stripe's webhook is. */
export function twilioInboundTextUrl(appBaseUrl: string): string {
  return `${appBaseUrl.replace(/\/+$/, '')}/api${TWILIO_INBOUND_TEXT_ROUTE}`;
}

/** 🔒 Decision 11780: the public route an inbound call is posted to, under the API's own root. */
export const TWILIO_INBOUND_VOICE_ROUTE = '/calls/inbound';

/** The address to paste into each number's voice webhook in Twilio's console. */
export function twilioInboundVoiceUrl(appBaseUrl: string): string {
  return `${appBaseUrl.replace(/\/+$/, '')}/api${TWILIO_INBOUND_VOICE_ROUTE}`;
}

/** 🔒 Decision 12001: the TwiML App's voice address, where the phone app's outbound call is posted. */
export const TWILIO_OUTBOUND_VOICE_ROUTE = '/calls/outbound';

/** The address to paste into the TwiML App's Voice request URL in Twilio's console. */
export function twilioOutboundVoiceUrl(appBaseUrl: string): string {
  return `${appBaseUrl.replace(/\/+$/, '')}/api${TWILIO_OUTBOUND_VOICE_ROUTE}`;
}

/** 🔒 Two tabs, Setup and Status, the address's anchor naming the open one; none opens Setup (as Zoho Books' tabs do). */
export const TWILIO_TABS = ['setup', 'status'] as const;
export type TwilioTab = (typeof TWILIO_TABS)[number];

export function twilioTabOf(hash: string | null | undefined): TwilioTab {
  const bare = (hash ?? '').replace(/^#/, '');
  return (TWILIO_TABS as readonly string[]).includes(bare) ? (bare as TwilioTab) : 'setup';
}

/** Where a value in force comes from: its Settings row, the environment's key where no row is held, or nowhere. */
export type TwilioSource = 'settings' | 'environment' | null;

/** 🔒 What the card reads beside the rows (decision 5820): the inbound address and where each value in force comes from, never a value. */
export interface TwilioSetupView {
  inboundTextUrl: string;
  /** Decision 11780: the inbound voice address; absent from an older answer. */
  inboundVoiceUrl?: string;
  /** Decision 12001: the TwiML App's voice address; absent from an older answer. */
  outboundVoiceUrl?: string;
  /** Decision 12000: whether the API key's SID and secret and the TwiML App's SID are held; absent from an older answer. */
  appCalling?: boolean;
  sources: Record<TwilioField, TwilioSource>;
}

/** 🔒 Test Connection's answer: the account Twilio names, or the refusal in Twilio's own words. */
export type TwilioCheck =
  | { ok: true; accountName: string; accountStatus: string | null }
  | { ok: false; refusal: 'notSetUp' | 'refused' | 'unreachable'; message: string | null };

/** An account SID is AC and thirty-two hexadecimal characters, as Twilio issues it. */
const ACCOUNT_SID_SHAPE = /^AC[0-9a-fA-F]{32}$/;
/** Decision 12000: an API key is SK, a TwiML App AP and a push credential CR, each with thirty-two hexadecimal characters. */
const API_KEY_SID_SHAPE = /^SK[0-9a-fA-F]{32}$/;
const TWIML_APP_SID_SHAPE = /^AP[0-9a-fA-F]{32}$/;
const PUSH_CREDENTIAL_SHAPE = /^CR[0-9a-fA-F]{32}$/;
/** A number in E.164: a plus, a country code that does not start with nought, and at most fifteen digits in all. */
const E164_SHAPE = /^\+[1-9][0-9]{6,14}$/;

/** The refusal a value of one of the card's rows earns, or null; a blank clears the row and the environment's key serves. */
export function twilioSettingProblem(key: string, value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  if (key === TWILIO_ACCOUNT_SID_KEY && (typeof value !== 'string' || !ACCOUNT_SID_SHAPE.test(value.trim()))) {
    return 'The account SID is the one Twilio shows in its console: AC and thirty-two letters and digits.';
  }
  if (key === TWILIO_FROM_NUMBER_KEY && (typeof value !== 'string' || !E164_SHAPE.test(value.trim()))) {
    return 'The company number is written in E.164, a plus and the country code, such as +15735550100.';
  }
  if (key === TWILIO_API_KEY_SID_KEY && (typeof value !== 'string' || !API_KEY_SID_SHAPE.test(value.trim()))) {
    return 'The API key SID is the one Twilio shows when the key is made: SK and thirty-two letters and digits.';
  }
  if (key === TWILIO_TWIML_APP_SID_KEY && (typeof value !== 'string' || !TWIML_APP_SID_SHAPE.test(value.trim()))) {
    return 'The TwiML App SID is the one Twilio shows on the app: AP and thirty-two letters and digits.';
  }
  if ((key === TWILIO_PUSH_CREDENTIAL_IOS_KEY || key === TWILIO_PUSH_CREDENTIAL_ANDROID_KEY) && (typeof value !== 'string' || !PUSH_CREDENTIAL_SHAPE.test(value.trim()))) {
    return 'A push credential SID is the one Twilio shows on the credential: CR and thirty-two letters and digits.';
  }
  if (key === VOICEMAIL_GREETING_KEY || key === MENU_WORDS_KEY) return phoneWordsProblem(value);
  if (key === PHONE_BUSINESS_HOURS_KEY) return phoneHoursProblem(value);
  if (key === RING_SECONDS_KEY || key === REP_FIRST_SECONDS_KEY) return ringSecondsProblem(value);
  return null;
}

/** 🔒 Decision 11780: whether a typed number is one in E.164, the shape every number on the card is held in. */
export function isE164(value: string): boolean {
  return E164_SHAPE.test(value.trim());
}
